import React from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import InputFormField from 'components/v2/form/InputFormField';
import AuthService from 'services/AuthService';
import { UpdateUserNameModel } from 'services/EpdClient';
import { HeaderDialog } from 'styles/Styles.styled';
import { firstNameValidationSchema, lastNameValidationSchema } from 'constants/userValidationSchemes';
import DialogClearStyles, { DialogClearStylesButtonsFooter } from './DialogClearStyles';
import { toaster } from 'components/v2/toast';
import useCurrentUser from 'hooks/useCurrentUser';

interface Props {
  visible: boolean;
  onHide(): void;
}

const ChangeUserNameDialog: React.FC<Props> = ({ visible, onHide }) => {
  const { t } = useTranslation();
  const validationSchema = Yup.object<UpdateUserNameModel>({
    firstName: firstNameValidationSchema(t),
    lastName: lastNameValidationSchema(t),
  });

  const user = useCurrentUser();
  const initialValues: UpdateUserNameModel = {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
  };

  const handleSubmit = async (values: UpdateUserNameModel) => {
    try {
      await AuthService.updateUserName(values);

      toaster({
        severity: 'success',
        summary: 'Account information updated successfully!',
      });
    } catch {
      toaster({
        severity: 'error',
        summary: 'Unable to update account information due to unexpected error.',
        details: 'Please try again later or contact support.',
      });
    }

    onHide();
  };

  return (
    <DialogClearStyles
      visible={visible}
      onHide={onHide}
      header={<HeaderDialog>Change user name</HeaderDialog>}
      closable={false}
      draggable={false}
    >
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({ handleSubmit, isValid, errors, dirty, touched, isSubmitting }) => (
          <DialogContent as={Form}>
            <InputsContainer>
              <InputFormField label="First name" name="firstName" errorPositioning="absolute" autoComplete="off" />
              <InputFormField label="Last name" name="lastName" errorPositioning="absolute" autoComplete="off" />
            </InputsContainer>

            <DialogFooter>
              <DialogClearStylesButtonsFooter>
                <SecondaryButton type="button" label="Cancel" onClick={() => onHide()} />
                <PrimaryButton
                  type="submit"
                  label={isSubmitting ? 'Saving...' : 'Save'}
                  onClick={() => handleSubmit()}
                  disabled={(!isValid && Object.keys(errors).some((error) => error in touched)) || !dirty || isSubmitting}
                />
              </DialogClearStylesButtonsFooter>
            </DialogFooter>
          </DialogContent>
        )}
      </Formik>
    </DialogClearStyles>
  );
};

export default ChangeUserNameDialog;

const DialogContent = styled.div`
  width: 90vw;
  max-width: 560px;
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 30px;
`;

const InputsContainer = styled.div`
  display: grid;
  gap: 30px;
`;

const DialogFooter = styled.div`
  margin-top: 30px;
`;
